import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, useMemo, type ReactNode } from 'react';
import { z } from 'zod';
import { sessionFromCustomer, type ConsoleArea, type Session } from '@/lib/api/console-contracts';
import { ApiError, cashierRequest, opsRequest } from '@/lib/api/http';
import {
  OperatorSessionResponseSchema,
  sessionFromOperator,
  type OperatorLoginRequest,
} from '@/lib/api/ops-session';
import { queryKeys } from '@/lib/query-client';
import type { Role } from '@pf/contracts';

/**
 * Authentication for a console that is deliberately two applications in one shell.
 *
 * SAD §11 describes two completely different auth problems, and only one of them is a human
 * signing in. This context handles that one — the operator plane's session, TOTP and RBAC — and,
 * because the customer area is the *website's* UI rather than PaymentFlow's, it also carries the
 * website's own customer session. They share a login screen and a shell; they do not share a
 * backend, a role model, or a set of permissions.
 *
 * ## Why the caller must name the plane
 *
 * `signInCustomer` and `signInOperator` are separate functions, and the login screen asks the user
 * which one they are. That is not a UX preference — it falls out of two hard constraints:
 *
 * 1. **The operator API needs the TOTP code in the same request as the password**
 *    (`POST /ops/v1/session`; see `lib/api/ops-session.ts`). So the console cannot ask "is this an
 *    operator?" first and challenge afterwards — there is nothing to ask.
 * 2. **An operator's password must never reach the participant's BFF.** Guessing the plane by
 *    trying the customer backend first would send PaymentFlow operator credentials to a system
 *    operated by somebody else. That is not a risk worth a saved click.
 *
 * The console previously guessed, by calling an operator login endpoint that only ever existed in
 * the MSW mock. Against the real API it 404s — and because the fallback to the customer plane only
 * fired on 401, a 404 took *customer* login down with it.
 *
 * The `area` on the resolved session is still what routes an identity to `/app/*` or `/ops/*`, and
 * is still a property of the identity rather than of the login form: picking "Operator" and
 * entering customer credentials fails, it does not grant operator access.
 */

/** What the auth context exposes. */
export interface AuthContextValue {
  /** The signed-in identity, or `null`. */
  session: Session | null;
  /** True while the initial session probe is in flight. */
  isLoading: boolean;
  /** Which area this identity belongs in. */
  area: ConsoleArea | null;
  /**
   * True when the signed-in operator holds the capability named by `role`.
   *
   * Deliberately **not** a hierarchy, because the server's RBAC is not one (SAD §11, and
   * `@pf/domain`'s `canPropose`): Admin manages participants/keys and *never* proposes or confirms
   * a review decision; Checker may both propose and confirm (on different cases); Maker only
   * proposes. Mirroring that here keeps the console from enabling a control the API will answer
   * with `403 INSUFFICIENT_ROLE`.
   *
   * SUPER is the one deliberate exception: the owner-tier account that does everything alone —
   * every admin surface plus propose *and* confirm (the server grants it every permission and
   * handles the dual-control bypass), so a SUPER session satisfies every capability here.
   */
  can: (role: Role) => boolean;
  /** Sign in to the participating website's customer area. */
  signInCustomer: (email: string, password: string) => Promise<Session>;
  /** Sign in to the PaymentFlow operator console. TOTP is mandatory and sent with the password. */
  signInOperator: (input: OperatorLoginRequest) => Promise<Session>;
  /** End the session. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Probe both planes for an existing session.
 *
 * The operator plane is asked first; on a 401 the website's BFF is asked. Two probes rather than
 * one because there genuinely are two session stores — pretending otherwise would be the exact
 * conflation D-I7 warns against.
 *
 * The two return *different shapes*, and that is not an accident to be smoothed over: the operator
 * session is PaymentFlow's (an id, a display name, an RBAC role) and the customer session is the
 * website's (`CustomerSession` in `@pf/bff-contract`, which has never heard of a role). Each is
 * widened into the shell's model in exactly one place.
 *
 * A 404 from the operator probe is treated like a 401 — a deployment that does not expose the
 * operator plane at all is a console with no operators, not a console that cannot sign anybody in.
 */
async function probeSession(): Promise<Session | null> {
  try {
    return sessionFromOperator(await opsRequest(OperatorSessionResponseSchema, '/session'));
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    if (!error.isUnauthenticated && error.status !== 404) throw error;
  }
  try {
    return sessionFromCustomer(await cashierRequest('getSession'));
  } catch (error) {
    if (error instanceof ApiError && (error.isUnauthenticated || error.status === 404)) return null;
    throw error;
  }
}

/** Provide the auth context to the app. */
export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const client = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: queryKeys.session,
    queryFn: probeSession,
    staleTime: 30_000,
    retry: false,
  });

  const adopt = async (session: Session): Promise<Session> => {
    client.setQueryData(queryKeys.session, session);
    await client.invalidateQueries();
    return session;
  };

  const customerLogin = useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      /*
       * End any operator session in this browser first.
       *
       * The shell holds exactly one identity, and `probeSession` resolves it by asking the
       * operator plane first — so a lingering operator cookie would silently outrank the customer
       * who just signed in, and land them on the dashboard. Best-effort: a 401 here means there
       * was nothing to end, which is the normal case.
       */
      // `z.void()`, not `z.null()`: the endpoint answers 204, and the HTTP layer parses a
      // no-content response as `undefined`.
      await opsRequest(z.void(), '/session', { method: 'DELETE' }).catch(() => undefined);
      return adopt(sessionFromCustomer(await cashierRequest('login', { body: input })));
    },
  });

  const operatorLogin = useMutation({
    mutationFn: async (input: OperatorLoginRequest) =>
      adopt(
        sessionFromOperator(
          await opsRequest(OperatorSessionResponseSchema, '/session', {
            method: 'POST',
            body: input,
          }),
        ),
      ),
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      /*
       * Both planes are ended server-side, and both have to be.
       *
       * The operator cookie is `HttpOnly; Path=/ops` with an 8-hour lifetime, so clearing the
       * client's cached session does not end the *session* — script cannot touch that cookie.
       * Skipping the call left an operator signed in for the rest of the day after they pressed
       * Sign out, and made the next sign-in on that browser land in the operator console no
       * matter whose credentials were used, because `probeSession` asks the operator plane first.
       */
      if (sessionQuery.data?.area === 'operator') {
        await opsRequest(z.void(), '/session', { method: 'DELETE' });
      } else {
        await cashierRequest('logout', { body: {} });
      }
    },
    onSuccess: () => {
      client.setQueryData(queryKeys.session, null);
      client.clear();
    },
  });

  const session = sessionQuery.data ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isLoading: sessionQuery.isLoading,
      area: session?.area ?? null,
      can: (role: Role) => {
        const current = session && session.area === 'operator' ? session.role : null;
        if (!current) return false;
        // The one deliberate exception to the no-hierarchy rule: SUPER holds every capability,
        // server-side included. Everything below stays a flat, non-hierarchical matrix.
        if (current === 'SUPER') return true;
        switch (role) {
          // Read access — every operator role, including Viewer (SAD §11 baseline).
          case 'VIEWER':
            return true;
          // Propose a review decision. The server's `canPropose`: Maker or Checker — never Admin.
          case 'MAKER':
            return current === 'MAKER' || current === 'CHECKER';
          // Confirm a proposal (dual control). Checker only.
          case 'CHECKER':
            return current === 'CHECKER';
          // Participants, keys, payee accounts, settings. Admin only — and Admin does nothing else.
          case 'ADMIN':
            return current === 'ADMIN';
          // Exactly role === SUPER — already answered true by the early return above.
          case 'SUPER':
            return false;
          default:
            return false;
        }
      },
      signInCustomer: (email: string, password: string) =>
        customerLogin.mutateAsync({ email, password }),
      signInOperator: (input: OperatorLoginRequest) => operatorLogin.mutateAsync(input),
      signOut: async () => {
        await logoutMutation.mutateAsync();
      },
    }),
    [session, sessionQuery.isLoading, customerLogin, operatorLogin, logoutMutation],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

/** Read the auth context. Throws outside {@link AuthProvider}, which is always a wiring bug. */
export function useAuth(): AuthContextValue {
  const value = use(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
